import React, { useState, useEffect } from "react";
import { ArrowRight, Check, ChevronRight, FileText } from "lucide-react";
import NexusLogo from "../core/NexusLogo";
import SourceSystemSelector from "./steps/SourceSystemSelector";
import ConnectionDetails from "./steps/ConnectionDetails";
import AnalysisGapDetection from "./steps/AnalysisGapDetection";
import QuestionnaireContainer from "./questionnaire/QuestionnaireContainer";
import NexusBrochure from "./marketing/NexusBrochure";
import { discoveryStepTitles } from "../../constants";

const NexusDashboard = ({ onNavigate }) => {
  const [currentView, setCurrentView] = useState("dashboard"); // 'dashboard' or 'brochure'
  const [currentStep, setCurrentStep] = useState(0);
  const [sourceSystem, setSourceSystem] = useState("");
  const [connected, setConnected] = useState(false);
  const [analysisFullyComplete, setAnalysisFullyComplete] = useState(false);
  const [showMasterProgress, setShowMasterProgress] = useState(true);

  // Initialize questionnaire state
  const [questionnaireData, setQuestionnaireData] = useState({
    orgProfile: {
      name: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      size: "100-1000",
      sector: "",
      industry: "",
      unionized: false,
      receivesGrants: false,
    },
    skuSelection: [],
    configSetup: {
      employeeTypes: [],
      cwTypes: [],
      payRateTypes: [],
      timeProfiles: [],
      licenseIdTypes: [],
      locationTypes: [],
      locationUsages: [],
      terminationReasons: [],
      managementLevels: [],
      otherIds: [],
    },
    foundationalSetup: {
      regions: [],
      companies: [],
      locations: [],
      costCenters: [],
      departments: [],
      gradeProfiles: [],
      jobProfiles: [],
    },
  });

  // Hide master progress when on questionnaire step
  useEffect(() => {
    if (currentStep === 2) {
      // Updated to hide on questionnaire step (now step 2)
      setShowMasterProgress(false);
    } else {
      setShowMasterProgress(true);
    }
  }, [currentStep]);

  // Check if current step is complete
  const isStepComplete = (stepIndex) => {
    switch (stepIndex) {
      case 0: // System Selection
        return sourceSystem !== "";
      case 1: // Initial Questionnaire
        return true; // For now, always allow to proceed
      case 2: // Connection Details
        return connected;
      case 3: // Analysis & Gap Detection
        return analysisFullyComplete;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (currentStep < discoveryStepTitles.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Function to go back to system selection from questionnaire
  const goBackToSystemSelection = () => {
    setCurrentStep(0); // Go to Source System Selection (first step)
  };

  // Toggle between dashboard and brochure views
  const toggleBrochure = () => {
    setCurrentView(currentView === "dashboard" ? "brochure" : "dashboard");
  };

  // If showing brochure, render it instead of regular dashboard
  if (currentView === "brochure") {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="p-4 flex justify-end">
          <button
            onClick={toggleBrochure}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 flex items-center"
          >
            <ArrowRight className="mr-2" size={16} />
            Back to Dashboard
          </button>
        </div>
        <NexusBrochure />
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <SourceSystemSelector
            sourceSystem={sourceSystem}
            setSourceSystem={setSourceSystem}
          />
        );
      case 1:
        return (
          <ConnectionDetails
            connected={connected}
            setConnected={setConnected}
            sourceSystem={sourceSystem}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <QuestionnaireContainer
            questionnaireData={questionnaireData}
            setQuestionnaireData={setQuestionnaireData}
            nextStep={nextStep}
            goBackToSystemSelection={goBackToSystemSelection}
          />
        );
      case 3:
        return (
          <AnalysisGapDetection
            setAnalysisFullyComplete={setAnalysisFullyComplete}
            analysisFullyComplete={analysisFullyComplete}
            questionnaireData={questionnaireData}
          />
        );
      default:
        return <div>Step not found</div>;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center">
          <NexusLogo className="w-12 h-12 mr-4" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Nexus Discovery Dashboard
            </h1>
            <p className="mt-1 text-gray-600">
              AI-Powered analysis for seamless Workday migration
            </p>
          </div>
          <span className="ml-4 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            PROTOTYPE
          </span>
        </div>

        {/* Brochure Link Button */}
        <button
          onClick={toggleBrochure}
          className="flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
        >
          <FileText size={18} className="mr-2" />
          View Marketing Brochure
        </button>
      </div>

      {/* Master Progress Steps - hidden during questionnaire */}
      {showMasterProgress && (
        <div className="mb-8">
          <ol className="flex items-center w-full">
            {discoveryStepTitles.map((step, index) => (
              <li
                key={index}
                className={`flex items-center ${
                  index < discoveryStepTitles.length - 1 ? "w-full" : ""
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                    currentStep > index
                      ? "bg-green-500 text-white"
                      : currentStep === index
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {currentStep > index ? (
                    <Check size={16} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    currentStep === index ? "text-blue-600" : "text-gray-500"
                  }`}
                >
                  {step}
                </span>

                {index < discoveryStepTitles.length - 1 && (
                  <div className="w-full flex items-center">
                    <div
                      className={`h-0.5 w-full mx-4 ${
                        currentStep > index ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                    <ChevronRight
                      className="shrink-0 text-gray-400"
                      size={16}
                    />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Current Step Content */}
      <div className="mb-8">{renderStepContent()}</div>

      {/* Navigation - only shown for master progress, not during questionnaire */}
      {showMasterProgress && currentStep !== 1 && (
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={currentStep === 0 ? () => onNavigate("modules") : prevStep}
          >
            {currentStep === 0 ? "Back to Modules" : "Back"}
          </button>

          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              isStepComplete(currentStep)
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            onClick={nextStep}
            disabled={
              !isStepComplete(currentStep) ||
              currentStep === discoveryStepTitles.length - 1
            }
          >
            Continue <ArrowRight className="ml-1 inline" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NexusDashboard;
